import RightRail, { Content, Footer, Header } from '../../libs/gs-component-right-rail';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import { ButtonDropdown, ButtonDropdownItem } from '../ButtonDropdown';
import Spinner from '@xo-union/tk-component-spinner';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ActionCompletedHouseholds, findLeader } from '../../helpers/households';
import DoubleActionWithError from '../FormComponents/DoubleActionWithError';
import { AddGuestEmail } from './AddGuestEmail';
import AddressHouseholdRailContent from './AddressHouseholdRailContent';
import { HouseholdRailProps } from './HouseholdRail';
import HouseholdRailZeroStateContent from './HouseholdRailZeroStateContent';
import styles from './rightRails.scss';
import { LegalSms } from './SmsSequence/legalSms';
import useShareTracking from './useShareTracking';
import { IconButton } from '../IconButton';
import { GuestFilters, TrackGuestFilters } from '../MessagingHub/GuestFilters';
import { ActionButton } from '../ButtonDropdown/ActionButton';

interface Props extends HouseholdRailProps {
  households: ActionCompletedHouseholds;
}

interface Val {
  title: string;
  subText: string;
  icon: string;
}

export interface Copy {
  individualGuest: Val;
  bulkEmail: Val;
}
export const copy: Copy = {
  individualGuest: {
    title: 'Add one guest',
    subText: 'Individually add a name and address.',
    icon: 'add_circle',
  },
  bulkEmail: {
    title: 'Add multiple guests',
    subText: 'Add a group of email addresses at once.',
    icon: 'email_alternate',
  },
};

const AddressHouseholdRail: React.FC<Props> = ({
  sendMessage,
  showSpinner,
  showSuccess,
  showError,
  errorMessage,
  onHideSuccess,
  setGuestRailOpen,
  setEditHousehold,
  setHouseholdsList,
  editRoute,
  households,
  householdRailType,
  householdLoading,
  RecaptchaComponent,
  SuccessComponent,
  setFilter,
  messageMedium,
  messageType,
}) => {
  const track = useShareTracking();
  const history = useHistory();
  const [selectedHouseholds, setSelectedHouseholds] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const mobileMedia = useMobileMedia();

  // Default to selecting all households with emails.
  useEffect(() => {
    const ids = new Set<string>();

    households.contactable.forEach(h => {
      const leader = findLeader(h)!;
      ids.add(leader.id);
    });

    setSelectedHouseholds(ids);
  }, [households]);

  const addMethod = [
    {
      type: 'individualGuest',
    },
    {
      type: 'bulkEmail',
    },
  ];

  const onAddEmailAddresses = () => {
    track('WWS Share Interaction', { action: 'bulk add emails initiated' });
    setShowModal(true);
  };

  const handleOpenGuestFilters = () => {
    trackGuestFilters({ action: 'open guest filter' });
    history.push(filtersRoute);
  };

  const trackBulkEditClicks = (action: string) => {
    track('Guest Messaging Interaction', {
      action: action,
      messageMedium: messageMedium,
      messageType: messageType,
    });
  };

  const trackGuestFilters: TrackGuestFilters = ({ action, selection }) => {
    track('Guest Messaging Interaction', {
      action: action,
      selection: selection,
      messageMedium: messageMedium,
      messageType: messageType,
    });
  };

  const filtersRoute: string = useMemo(() => editRoute.replace('/edit', '/filters'), [editRoute]);

  const addGuestButtonProps = useMemo(() => {
    if (mobileMedia.yes) {
      return {
        icon: 'add',
        size: 'md',
      };
    }
    return {
      text: 'Add guests',
    };
  }, [mobileMedia.yes]);

  const actionButtonProps = useMemo(() => {
    if (mobileMedia.yes) {
      return {
        icon: 'add',
        size: 'md',
      };
    }
    return {
      actionButton: true,
      text: 'Add Guest',
    };
  }, [mobileMedia.yes]);

  const showZeroState =
    !households.contactable.length && !households.unContactable.length && !households.actionCompleted.length;

  return (
    <RightRail open={true} overlay={false} className={styles.rightRail}>
      {showModal && <AddGuestEmail onClose={() => setShowModal(false)} />}
      <Header>
        <div>Add recipients</div>
        {showZeroState ? null : (
          <div className={styles.addGuestLink}>
            {householdRailType === 'email' ? (
              <ButtonDropdown
                actionButton={true}
                size="lg"
                color="primary"
                data-testid="add-guests-button"
                {...addGuestButtonProps}
              >
                {addMethod?.map(({ type }) => {
                  return (
                    <ButtonDropdownItem
                      key={type}
                      onClick={() => {
                        if (type === 'individualGuest') {
                          setGuestRailOpen(true);
                        } else {
                          onAddEmailAddresses();
                        }
                      }}
                      {...copy[type as keyof Copy]}
                    />
                  );
                })}
              </ButtonDropdown>
            ) : (
              <ActionButton
                onClick={() => {
                  setHouseholdsList([]);
                  setGuestRailOpen(true);
                }}
                expanded={false}
                hasPopup={false}
                {...actionButtonProps}
              />
            )}
            {mobileMedia.yes ? (
              <IconButton
                name="filter"
                size="md"
                arialabel="Filter guests"
                datatestid="filter-guests-button"
                onClick={handleOpenGuestFilters}
              />
            ) : null}
          </div>
        )}
      </Header>
      {householdLoading ? (
        <Content className={styles.spinner}>
          <Spinner />
        </Content>
      ) : (
        <>
          {showZeroState ? (
            <HouseholdRailZeroStateContent
              setGuestRailOpen={setGuestRailOpen}
              onAddEmailAddresses={onAddEmailAddresses}
              householdRailType={householdRailType}
            />
          ) : (
            <>
              <GuestFilters
                setFilterCriteria={setFilter}
                filtersPath={filtersRoute}
                trackGuestFilters={trackGuestFilters}
              />
              <AddressHouseholdRailContent
                households={households}
                setEditHousehold={setEditHousehold}
                setHouseholdsList={setHouseholdsList}
                setSelectedHouseholds={setSelectedHouseholds}
                selectedHouseholds={selectedHouseholds}
                setGuestRailOpen={setGuestRailOpen}
                onClickBulkEdit={trackBulkEditClicks}
                type={householdRailType}
              />
            </>
          )}
        </>
      )}
      {householdRailType === 'phone' && <LegalSms />}
      <Footer>
        <DoubleActionWithError
          primaryOnClick={() => {
            sendMessage(Array.from(selectedHouseholds));
          }}
          primaryText="Send Message"
          primaryDisabled={selectedHouseholds.size === 0}
          successText="Message sent!"
          errorText={errorMessage ? errorMessage : 'Something went wrong! Your message may not have been sent.'}
          showSpinner={showSpinner}
          showSuccess={showSuccess}
          showError={showError}
          onHideSuccess={onHideSuccess}
        />
        {RecaptchaComponent}
        {SuccessComponent}
      </Footer>
    </RightRail>
  );
};

export default AddressHouseholdRail;
